
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Heart, Loader2 } from 'lucide-react';

interface Tag {
  id: string;
  name: string;
  article_count: number;
  slug: string;
  description?: string;
}

interface TopicListViewProps {
  topics: Tag[];
  loading: boolean;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  setSelectedTopic: (topic: string | null) => void;
  toggleFollowTopic: (topicId: string) => void;
  followedTopics: string[];
}

const TopicListView: React.FC<TopicListViewProps> = ({
  topics,
  loading,
  searchQuery,
  setSearchQuery,
  setSelectedTopic,
  toggleFollowTopic,
  followedTopics
}) => {
  const filteredTopics = topics.filter(topic => 
    topic.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading topics...</span>
      </div>
    );
  }

  if (filteredTopics.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[200px] border rounded-lg p-6">
        <p className="text-muted-foreground">No topics found matching your search</p>
        {searchQuery && (
          <Button variant="ghost" onClick={() => setSearchQuery("")} className="mt-2">
            Clear search
          </Button>
        )}
      </div>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <div className="divide-y">
          {filteredTopics.map(topic => (
            <div 
              key={topic.id} 
              className="p-4 hover:bg-accent cursor-pointer"
              onClick={() => setSelectedTopic(topic.name)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium">{topic.name}</h3>
                    {topic.article_count > 50 && (
                      <Badge variant="outline" className="bg-red-100 text-red-600 border-red-200">
                        Hot
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                    <span>{topic.article_count} articles</span>
                  </div>
                  {topic.description && (
                    <p className="text-sm mt-2">{topic.description}</p>
                  )}
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFollowTopic(topic.id);
                  }}
                >
                  {followedTopics.includes(topic.id) ? (
                    <Heart className="h-4 w-4 fill-primary text-primary" />
                  ) : (
                    <Heart className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default TopicListView;
